import { BookingInfo } from "@/app/(app)/[bookingType]/context/bookingProvider";
import {
  PaymentMethod,
  TravelProfile,
  TravelProfileResponse,
  UserTravelProfilesPostRequest,
  UserTravelProfilesTravelProfileIdPutRequest,
} from "@/generated/email_parser.openapi";
import { handleError, reportError } from "@/lib/apiUtils";
import { isValidName, normalizeEmail } from "./utils";

export type PersonalDetails = {
  firstName: string;
  lastName: string;
  emailAddress: string;
  phoneNumber?: string;
};

export const fetchPaymentMethods = async (): Promise<PaymentMethod[]> => {
  const methods = await fetch("/api/user/payment_method/get").then((res) =>
    res.json()
  );
  return methods.sort((a: PaymentMethod) => (a._default ? -1 : 1));
};

export const fetchTravelerInfo = async (): Promise<
  TravelProfileResponse | undefined
> => {
  try {
    const response = (await fetch("/api/user/traveler/get").then((res) =>
      res.json()
    )) as TravelProfileResponse;
    return response;
  } catch (error) {
    handleError("Failed to fetch traveler info", {
      reportError: true,
      silent: true,
    });
    return undefined;
  }
};

const normalizeNamePart = (value: string | null | undefined): string =>
  (value ?? "").trim().toLowerCase();

export const createOrUpdateTravelerInfo = async (
  contactInfo: PersonalDetails,
  userId: string
): Promise<void> => {
  try {
    const existingProfiles = await fetchTravelerInfo();

    // Throw rather than fall through to a create. Falling through is how
    // transient network blips on the GET became permanent duplicate rows —
    // the dedup branch was skipped and a fresh insert always ran. The
    // booking-submit handler catches this and surfaces it to the user.
    // `== null` deliberately covers both undefined (fetch threw) and null
    // (API returned a literal null JSON body).
    if (existingProfiles == null) {
      throw new Error("Failed to fetch existing traveler profiles");
    }

    // Match on (email, first_name, last_name) so distinct travelers sharing
    // an email (e.g. user booking on behalf of a family member) don't
    // overwrite each other's profile.
    const normalizedEmail = normalizeEmail(contactInfo.emailAddress);
    const normalizedFirst = normalizeNamePart(contactInfo.firstName);
    const normalizedLast = normalizeNamePart(contactInfo.lastName);

    const existingProfile = existingProfiles.travelProfiles?.find((p) => {
      return (
        normalizeEmail(p.email) === normalizedEmail &&
        normalizeNamePart(p.firstName) === normalizedFirst &&
        normalizeNamePart(p.lastName) === normalizedLast
      );
    });

    if (existingProfile && existingProfile.id) {
      // Profile already exists, update it instead of creating duplicate
      const updateRequest: UserTravelProfilesTravelProfileIdPutRequest = {
        travelProfileId: existingProfile.id,
        travelProfile: {
          id: existingProfile.id,
          email: contactInfo.emailAddress,
          firstName: contactInfo.firstName,
          lastName: contactInfo.lastName,
          phoneNumber: contactInfo.phoneNumber,
          userId: userId,
        },
      };

      const updateResponse = await fetch("/api/user/traveler/update", {
        method: "POST",
        body: JSON.stringify(updateRequest),
      });

      if (!updateResponse.ok) {
        const errorData = await updateResponse.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `Failed to update travel profile: ${updateResponse.statusText}`
        );
      }

      return;
    }

    // No existing profile, create new one
    const request: UserTravelProfilesPostRequest = {
      travelProfile: {
        email: contactInfo.emailAddress,
        firstName: contactInfo.firstName,
        lastName: contactInfo.lastName,
        phoneNumber: contactInfo.phoneNumber,
        userId: userId,
      },
    };

    const createResponse = await fetch("/api/user/traveler/add", {
      method: "POST",
      body: JSON.stringify(request),
    });

    if (!createResponse.ok) {
      const errorData = await createResponse.json().catch(() => ({}));
      throw new Error(
        errorData.message ||
          `Failed to create travel profile: ${createResponse.statusText}`
      );
    }
  } catch (error) {
    handleError(
      error instanceof Error
        ? error.message
        : "Failed to create or update travel profile",
      {
        reportError: true,
      }
    );
    throw error; // Re-throw to allow calling code to handle the error
  }
};

export const parseTravelerInfoToContactInfo = (
  travelerInfo: TravelProfile,
  defaultEmail: string = "",
  defaultFirstName: string = "",
  defaultLastName: string = ""
): PersonalDetails => {
  return {
    firstName: defaultFirstName || travelerInfo.firstName || "",
    lastName: defaultLastName || travelerInfo.lastName || "",
    emailAddress: defaultEmail || travelerInfo.email || "",
    phoneNumber: travelerInfo.phoneNumber || "",
  };
};

export const validateContactInfo = (contactInfo: PersonalDetails): string => {
  if (!contactInfo.firstName || !contactInfo.firstName.trim())
    return "First name is required.";
  if (!isValidName(contactInfo.firstName))
    return "First name contains invalid characters.";
  if (!contactInfo.lastName || !contactInfo.lastName.trim())
    return "Last name is required.";
  if (contactInfo.lastName.trim().length < 2)
    return "Family name must be at least 2 characters long.";
  if (!isValidName(contactInfo.lastName))
    return "Last name contains invalid characters.";
  if (!contactInfo.emailAddress) return "Email address is required.";

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(contactInfo.emailAddress))
    return "Valid email address is required.";

  if (!contactInfo.phoneNumber) return "Phone number is required.";
  return "";
};

export const handleContactInfoChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  prevState: PersonalDetails
): PersonalDetails => {
  const { name, value } = e.target;

  return { ...prevState, [name]: value };
};

/**
 * Fetches the customer's IP address using the ipify.org API
 * @returns Promise that resolves to the customer's IP address or null if there was an error
 */
export const getCustomerIp = async (): Promise<string | null> => {
  try {
    const response = await fetch("https://api.ipify.org/?format=json");
    if (!response.ok) {
      return null;
    }
    const data = await response.json();
    return data.ip;
  } catch (error) {
    reportError(error);
    return null;
  }
};

export const buildOccupancyString = (
  bookingInfo: Partial<Pick<BookingInfo, "numberOfAdults" | "childrenAges">>
) => {
  const adults = bookingInfo?.numberOfAdults;
  const children = bookingInfo?.childrenAges?.length || 0;
  let occupancy = "";
  if (adults || children) {
    const parts = [];
    if (adults) parts.push(`${adults} Adult${adults > 1 ? "s" : ""}`);
    if (children) parts.push(`${children} Child${children > 1 ? "ren" : ""}`);
    occupancy = parts.join(", ");
  }
  return occupancy;
};

/**
 * Strip non-alphanumeric characters from a loyalty ID before sending to
 * suppliers (e.g. Travelport rejects "#324673267").
 * Returns undefined when the cleaned value is empty.
 */
export const sanitizeLoyaltyId = (
  loyaltyId: string | null | undefined
): string | undefined => {
  if (!loyaltyId) return undefined;
  const cleaned = loyaltyId.replace(/[^a-zA-Z0-9]/g, "");
  return cleaned || undefined;
};

type OccupancyCounts = {
  numberOfAdults?: number;
  childrenAges?: number[];
};

// The frozen booked-rate snapshot preserves children that supplier itineraries
// may omit. Reject malformed snapshots instead of applying search defaults.
export function getBookedOccupancy(
  snapshot: unknown,
  itinerary: OccupancyCounts
): OccupancyCounts {
  if (
    typeof snapshot !== "string" ||
    !/^[1-9]\d*(?:-(?:0|[1-9]\d*)(?:,(?:0|[1-9]\d*))*)?$/.test(snapshot)
  ) {
    return itinerary;
  }
  const [adults, ages] = snapshot.split("-");
  const numberOfAdults = Number(adults);
  const childrenAges = ages === undefined ? [] : ages.split(",").map(Number);
  if (
    !Number.isSafeInteger(numberOfAdults) ||
    !childrenAges.every(Number.isSafeInteger)
  ) {
    return itinerary;
  }
  return { numberOfAdults, childrenAges };
}
